/*
 * Carbisdale - Screen-Modul
 *
 * Verwaltet das Datenmodell des Bildschirms:
 * Ein zweidimensionales Grid aus ScreenCharacter-Objekten.
 *
 * HINWEIS: Im produktiven Layout-basierten Render-Pfad wird das Grid (setChar/getChar)
 * nicht mehr aktiv genutzt. Hauptsaechliche Funktion ist heute:
 * - Dimensionen (cols/rows) berechnen
 * - Dirty-Flag fuer Rendering-Optimierung
 * Das Grid bleibt als Fallback fuer den Legacy-Render-Pfad (ohne LayoutManager) erhalten.
 *
 * HINWEIS: createScreenCharacter ist jetzt in config.js definiert.
 * Dieser Re-Export ist für Abwärtskompatibilität.
 */

import { Style, DUMMY_CHARS, createScreenCharacter } from './config.js'

// Re-Export für Abwärtskompatibilität (andere Module können weiterhin aus screen.js importieren)
export { createScreenCharacter }

/**
 * Screen-Klasse
 *
 * Das zentrale Datenmodell für den Terminal-Bildschirm.
 * Enthält ein Grid aus Zeichen und verwaltet dessen Dimensionen.
 */
export class Screen {
    /**
     * Erstellt einen neuen Screen mit den gegebenen Dimensionen.
     *
     * @param {number} cols - Anzahl Spalten
     * @param {number} rows - Anzahl Zeilen
     */
    constructor(cols, rows) {
        this.cols = cols
        this.rows = rows
        this.grid = []
        this.isDirty = true  // Initial immer rendern

        this._initializeGrid()
    }

    /**
     * Initialisiert das Grid mit leeren Zeichen.
     * Wird beim Erstellen und nach Resize aufgerufen.
     */
    _initializeGrid() {
        this.grid = []
        for (let y = 0; y < this.rows; y++) {
            const row = []
            for (let x = 0; x < this.cols; x++) {
                row.push(createScreenCharacter())
            }
            this.grid.push(row)
        }
    }

    /**
     * Setzt ein Zeichen an der angegebenen Position.
     *
     * @param {number} x - Spalten-Position (0-basiert)
     * @param {number} y - Zeilen-Position (0-basiert)
     * @param {string} char - Das Zeichen
     * @param {string} style - Style-Name aus Style enum
     * @returns {boolean} true wenn erfolgreich, false wenn außerhalb des Grids
     */
    setChar(x, y, char, style = Style.DEFAULT) {
        if (!this._isValidPosition(x, y)) {
            return false
        }

        // Mutation statt Neuallokation (vermeidet 10.000 Objekt-Erstellungen pro Frame)
        const cell = this.grid[y][x]
        cell.char = char
        cell.style = style
        this.isDirty = true
        return true
    }

    /**
     * Liest das Zeichen an der angegebenen Position.
     *
     * @param {number} x - Spalten-Position
     * @param {number} y - Zeilen-Position
     * @returns {Object|null} ScreenCharacter oder null wenn ungültig
     */
    getChar(x, y) {
        if (!this._isValidPosition(x, y)) {
            return null
        }
        return this.grid[y][x]
    }

    /**
     * Prüft ob eine Position innerhalb des Grids liegt.
     *
     * @param {number} x - Spalten-Position
     * @param {number} y - Zeilen-Position
     * @returns {boolean}
     */
    _isValidPosition(x, y) {
        return x >= 0 && x < this.cols && y >= 0 && y < this.rows
    }

    /**
     * Ändert die Grid-Dimensionen (z.B. nach Window-Resize).
     *
     * Bei Verkleinerung wird Content abgeschnitten.
     * Bei Vergrößerung werden neue Zellen mit Leerzeichen gefüllt.
     *
     * @param {number} newCols - Neue Spaltenanzahl
     * @param {number} newRows - Neue Zeilenanzahl
     */
    resize(newCols, newRows) {
        // Altes Grid sichern
        const oldGrid = this.grid
        const oldCols = this.cols
        const oldRows = this.rows

        // Neue Dimensionen setzen
        this.cols = newCols
        this.rows = newRows

        // Neues Grid erstellen
        this.grid = []
        for (let y = 0; y < newRows; y++) {
            const row = []
            for (let x = 0; x < newCols; x++) {
                // Alten Content übernehmen wenn vorhanden, sonst Leerzeichen
                if (y < oldRows && x < oldCols) {
                    row.push(oldGrid[y][x])
                } else {
                    row.push(createScreenCharacter())
                }
            }
            this.grid.push(row)
        }

        this.isDirty = true
    }

    /**
     * Füllt das gesamte Grid mit Dummy-Content.
     * Verwendet die Zeichenfolge aus config.js fortlaufend.
     *
     * Nützlich zum Testen des Renderings.
     */
    fillWithDummyContent() {
        let charIndex = 0

        for (let y = 0; y < this.rows; y++) {
            for (let x = 0; x < this.cols; x++) {
                const char = DUMMY_CHARS[charIndex % DUMMY_CHARS.length]

                // Abwechselnde Styles für visuellen Test
                // Jede Zeile ein anderer Style (mit verschiedenen Intensitäten)
                let style
                switch (y % 3) {
                    case 0:
                        style = Style.DUMMY_LOW
                        break
                    case 1:
                        style = Style.DUMMY_MEDIUM
                        break
                    case 2:
                        style = Style.DUMMY_HIGH
                        break
                }

                this.setChar(x, y, char, style)
                charIndex++
            }
        }

        this.isDirty = true
    }

    /**
     * Leert das gesamte Grid (alle Zeichen werden zu Leerzeichen).
     */
    clear() {
        for (let y = 0; y < this.rows; y++) {
            for (let x = 0; x < this.cols; x++) {
                const cell = this.grid[y][x]
                cell.char = ' '
                cell.style = Style.DEFAULT
            }
        }
        this.isDirty = true
    }

    /**
     * Markiert den Screen als "muss neu gerendert werden".
     */
    markDirty() {
        this.isDirty = true
    }

    /**
     * Setzt das Dirty-Flag zurück (nach dem Rendern aufrufen).
     */
    markClean() {
        this.isDirty = false
    }
}
